import { AlertCircle, CheckCircle, Loader } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import "react-phone-number-input/style.css";
import Swal from "sweetalert2";
import authService from "../../api/authService";

import * as Sentry from "@sentry/react";
import professionalProfileApi from "../../api/professionalProfile";
import { tabs } from "../../constants/tabs";
import { useAuthStore } from "../../store/authStore.js";
import AviationCategoryTab from "../professional/aviationCategory/AviationCategoryTab";
import CVTab from "../professional/cv/CVTab";
import DocumentsTab from "../professional/documents/DocumentsTab";
import EmploymentHistoryTab from "../professional/employmentHistory/EmploymentHistoryTab";
import CurrentPositionCard from "../professional/experience/CurrentPositionCard";
import CurrentPositionDialog from "../professional/experience/CurrentPositionDialog";
import ExperienceTab from "../professional/experience/ExperienceTab";
import IcaoLanguageProficiencyTab from "../professional/icao/IcaoLanguageProficiencyTab";
import LicenseTab from "../professional/licenses/LicensesTab";
import PersonalInfoTab from "../professional/personalInfo/PersonalInfoTab";
import ProfessionalRoleTab from "../professional/professionalRole/ProfessionalRoleTab";
import QualificationsTab from "../professional/qualifications/QualificationsTab";
import RegulatoryBodyTab from "../professional/regulatoryBodies/RegulatoryBodyTab";
import VisibilityTab from "../professional/VisibilityTab";

const ManageProfilePage = () => {
  // const { user, profileCompletion } = useAuthStore(s => ({ user: s.user, profileCompletion: s.profileCompletion }));
  const user = useAuthStore(s => s.user);
  const profileCompletion = useAuthStore(s => s.profileCompletion());
  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("cv");
  const [formData, setFormData] = useState(null);
  const [fieldChoices, setFieldChoices] = useState(null);
  const [isProfileHidden, setIsProfileHidden] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [documents, setDocuments] = useState([]);

  const baseUrl = import.meta.env.VITE_BACKEND_DOMAIN || "http://localhost:8000";
  const profileImgUrl = baseUrl + profilePicturePreview;

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await authService.getProfessionalProfile();

      const initializedData = {
        ...data,
        full_name:
          data.full_name ||
          `${data.first_name || ""} ${data.last_name || ""}`.trim(),
        personal_info: data.personal_info || {},
        experience: data.experience || {},
        documents: data.documents || {},
        professional_roles: data.professional_roles || [],
        qualifications: data.qualifications || [],
        licenses: data.licenses || [],
        employment_history: data.employment_history || [],
      };
      setFormData(initializedData);
      setProfilePicturePreview(data.profile_picture || null);
      setIsProfileHidden(data.is_profile_hidden || false);
    } catch (error) {
      Sentry.captureException(error, "Could not load profile data");
      showNotification("Could not load profile data.", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await professionalProfileApi.getDocuments();
      setDocuments(res.data);
    } catch (error) {
      Sentry.captureException(error, "Could not load documents");
      showNotification("Could not load documents", "error");
    }
  }, []);

  const fetchFieldChoices = useCallback(async () => {
    try {
      const res = await professionalProfileApi.getFieldChoices();
      setFieldChoices(res.data);
    } catch (error) {
      Sentry.captureException(error, "Could not load field choices");
      showNotification("Could not load form options", "error");
    }
  }, []);

  useEffect(() => {
    if (user?.role === "professional") {
      fetchProfile();
      fetchDocuments();
      fetchFieldChoices();
    } else {
      setIsLoading(false);
    }
  }, [fetchProfile, user, fetchDocuments, fetchFieldChoices]);

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: "", type: "" }), 4000);
  };

  const handleVisibilityToggle = () => {
    setActiveTab('visibility');
    // Optional: scroll to top if needed
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading || !fieldChoices)
    return (
      <div className="text-center py-20">
        <Loader className="w-12 h-12 text-primary-500 animate-spin mx-auto" />
      </div>
    );
  if (!formData)
    return (
      <div className="text-center py-20 text-red-600 font-semibold">
        Could not load profile. Please try refreshing the page.
      </div>
    );

  const renderContent = () => {
    switch (activeTab) {
      case "personal":
        return (
          <PersonalInfoTab
            onModification={fetchProfile}
            personalInfo={formData.personal_info}
            additionalData={{
              profile_picture: formData.profile_picture,
              full_name: formData.full_name,
              first_name: formData.first_name,
              last_name: formData.last_name,
              email: formData.email,
              social_media_links: formData.social_media_links
            }}
            setNotification={showNotification}
            dropDownOptions={{
              regionChoices: fieldChoices.region_choices,
              languageChoices: fieldChoices.language_choices,
              availabilityChoices: fieldChoices.availability_choices,
            }}
          />
        );
      case "experience":
        return (
          <>
            <CurrentPositionCard
              data={formData.experience}
              onEdit={() => setOpenDialog(true)}
              dropDownOptions={{
                aircraftTypeChoices: fieldChoices.aircraft_type_choices,
              }}
            />

            <CurrentPositionDialog
              open={openDialog}
              onClose={() => setOpenDialog(false)}
              data={formData.experience}
              onSave={fetchProfile}
              setNotification={showNotification}
              dropDownOptions={{
                aircraftTypeChoices: fieldChoices.aircraft_type_choices,
              }}
            />
          </>
        );
      case "experience2":
        return (
          <>
            <ExperienceTab
              onModification={fetchProfile}
              onDocumentUpload={fetchDocuments}
              experiences={[
                ...(formData.experiences || []),
                ...(formData.licenses || [])
                  .filter(
                    (lic) =>
                      !new Set(
                        (formData.experiences || []).map((e) => e.license_id).filter(Boolean)
                      ).has(lic.id)
                  )
                  .map((lic) => ({
                    ...lic,
                    license_id: lic.id,
                    id: null,
                    isStandaloneLicense: true,
                  })),
              ]}
              setNotification={showNotification}
              dropDownOptions={{
                licenseTypeChoices: fieldChoices.license_type_choices,
                issuingAuthorityChoices: fieldChoices.issue_authority_choices,
                licenseStatusChoices: fieldChoices.license_status_choices,
                ratingChoices: fieldChoices.rating_choices,
                pilotTypes: fieldChoices.pilot_type_choices,
                aircraftTypeChoices: fieldChoices.aircraft_type_choices,
                aviationCategoryChoices: fieldChoices.aviation_category_choices
              }}
            />
          </>
        );
      case "documents":
        return (
          <DocumentsTab
            documentFiles={documents}
            onModification={fetchProfile}
            onDocumentUpload={fetchDocuments}
            setNotification={showNotification}
            dropDownOptions={{
              documentCategories: fieldChoices.document_categories,
            }}
          />
        );
      case "cv":
        return (
          <CVTab
            documentFiles={documents}
            onModification={fetchProfile}
            onDocumentUpload={fetchDocuments}
            setNotification={showNotification}
          />
        );
      case "professional":
        return (
          <ProfessionalRoleTab
            onModification={fetchProfile}
            roles={formData.professional_roles}
            setNotification={showNotification}
            dropDownOptions={{
              aviationCategoryChoices: fieldChoices.aviation_category_choices,
              icaoIssuingAuthorityChoices:
                fieldChoices.icao_issuing_authority_choices,
            }}
          />
        );
      case "qualifications":
        return (
          <QualificationsTab
            onModification={fetchProfile}
            onDocumentUpload={fetchDocuments}
            qualifications={formData.qualifications}
            setNotification={showNotification}
            dropDownOptions={{
              developmentCourseChoices: fieldChoices.development_course_choices,
              aviationSpecificationChoices:
                fieldChoices.aviation_specification_choices,
              workshopChoices: fieldChoices.workshops_attended_choices,
              educationLevelChoices: fieldChoices.education_level_choices,
            }}
          />
        );

      case "licenses":
        return (
          <LicenseTab
            onModification={fetchProfile}
            licenses={formData.licenses}
            setNotification={showNotification}
            dropDownOptions={{
              licenseTypeChoices: fieldChoices.license_type_choices,
              issuingAuthorityChoices: fieldChoices.issue_authority_choices,
              licenseStatusChoices: fieldChoices.license_status_choices,
              ratingChoices: fieldChoices.rating_choices,
            }}
          />
        );
      case "employment_history":
        return (
          <EmploymentHistoryTab
            employmentHistories={formData.employment_history}
            onModification={fetchProfile}
            setNotification={showNotification}
            dropDownOptions={{

            }}
          />
        );

      case "aviation_category":
        return (
          <AviationCategoryTab
            aviationCategories={formData.aviation_categories}
            onModification={fetchProfile}
            setNotification={showNotification}
            dropDownOptions={{
              airTrafficServiceChoices:
                fieldChoices.air_traffic_services_choices,
              cabinInflightChoices: fieldChoices.cabin_inflight_choices,
              flightCrewChoices: fieldChoices.flight_crew_choices,
              flightOpsDispatchChoices:
                fieldChoices.flight_ops_dispatch_choices,
              managementRegulatoryChoices:
                fieldChoices.management_regulatory_choices,
              medicalChoices: fieldChoices.medical_choices,
            }}
          />
        );
      case "regulatory_body":
        return (
          <RegulatoryBodyTab
            onModification={fetchProfile}
            regulatoryBodies={formData.regulatory_bodies}
            setNotification={showNotification}
            dropDownOptions={{
              regulatoryBodyRegionChoices:
                fieldChoices.regulatory_body_region_choices,
            }}
          />
        );
      case "icao_language_proficiency":
        return (
          <IcaoLanguageProficiencyTab
            icao={formData.icao_language_proficiencies}
            onModification={fetchProfile}
            setNotification={showNotification}
            dropDownOptions={{
              icaoRecognizedLanguageChoices:
                fieldChoices.icao_recognized_languages,
              icaoLevelChoices: fieldChoices.icao_level_choices,
              icaoIssuingAuthorityChoices:
                fieldChoices.icao_issuing_authority_choices,
            }}
          />
        );
      case "visibility":
        return <VisibilityTab isGloballyHidden={isProfileHidden} setIsGloballyHidden={setIsProfileHidden} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Top wide header (uses uploaded image) */}
      <div
        className="w-full bg-cover bg-center h-40 sm:h-48 md:h-56 relative"
        style={{
          backgroundImage: `url('/images/aeroplane.jpg')`,
          objectFit: "contain",
        }}
      >
        <div className="backdrop-brightness-90 w-full h-full flex items-center">
          <div className="max-w-7xl mx-auto w-full px-4 py-4 sm:py-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 flex items-center gap-4 max-w-3xl">
              <img
                src={profileImgUrl || "/images/user.png"}
                alt="profile"
                className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
              />
              <div>
                <div className="text-lg font-semibold text-gray-800">
                  {formData.full_name}
                </div>
                <div className="text-sm text-gray-600">{formData.email}</div>
              </div>

              <div className="ml-auto hidden sm:flex flex-col items-end gap-2">


                <div className="text-sm text-gray-600">Profile Completion</div>
                <div className="w-36 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-primary-600"
                    style={{ width: `${profileCompletion}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {notification.message && (
          <div
            className={`fixed top-20 right-6 z-50 p-4 rounded-md shadow-lg flex items-center space-x-3 
              ${notification.type === "success"
                ? "bg-green-50 border border-green-300 text-green-700"
                : "bg-red-50 border border-red-300 text-red-700"
              }`}
          >
            {notification.type === "success" ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Manage Profile
            </h1>
            <p className="mt-1 text-gray-500">
              Keep your professional information up to date to attract the best
              opportunities.
            </p>
          </div>
          <button
            onClick={handleVisibilityToggle}
            className={`group relative flex flex-col items-start gap-1 px-5 py-3 rounded-2xl text-sm font-semibold transition-all border shadow-lg hover:shadow-xl active:scale-95 overflow-hidden ${isProfileHidden
              ? 'bg-white/80 backdrop-blur-md text-slate-600 border-slate-200 hover:bg-slate-50'
              : 'bg-emerald-50/90 backdrop-blur-md text-emerald-900 border-emerald-200'
              }`}
            title={'Click to manage profile visibility'}
          >
            {/* Subtle background glow on hover */}
            <div className={`absolute -inset-1 opacity-0 group-hover:opacity-20 transition-opacity blur-2xl ${isProfileHidden ? 'bg-slate-400' : 'bg-emerald-400'}`} />

            <div className="flex items-center gap-3 relative z-10">
              <div className="relative">
                <div className={`w-3 h-3 rounded-full ${isProfileHidden ? 'bg-slate-400' : 'bg-emerald-500'}`} />
                {!isProfileHidden && (
                  <div className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-400 animate-ping opacity-75" />
                )}
              </div>
              <p className="tracking-tight">
                {isProfileHidden ? 'Profile Private' : 'Profile Public'}
              </p>
            </div>
            <p className="text-[11px] font-medium opacity-60 relative z-10 pl-6">
              {isProfileHidden ? 'Restricted Visibility' : 'Visible to Recruiters'}
            </p>
          </button>
        </div>

        {/* Main grid: sidebar + content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <aside className="col-span-1">
            <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 p-3 sticky top-24">
              <div className="divide-y divide-gray-100">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left py-3 px-3 flex items-center gap-3 text-sm font-medium rounded-lg transition-colors focus:outline-none 
                      ${activeTab === tab.id
                        ? "bg-primary-50 text-primary-700 border border-primary-100"
                        : "text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="truncate">{tab.name}</span>
                  </button>
                ))}
              </div>

              {/* Action buttons on mobile to toggle edit/save (kept commented to avoid logic changes) */}
              <div className="mt-4 hidden lg:block">
                {/* Placeholders for any persistent sidebar controls if needed */}
              </div>
            </div>

            {/* Compact tabs for small screens (horizontal) */}
            <div className="mt-4 lg:hidden">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 overflow-x-auto">
                <div className="flex space-x-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 flex items-center space-x-2 py-2 px-3 text-sm font-semibold rounded-md transition 
                        ${activeTab === tab.id
                          ? "bg-primary-600 text-white"
                          : "text-gray-600 bg-white border border-gray-100 hover:bg-gray-50"
                        }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Content area */}
          <main className="col-span-1 lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="space-y-8">{renderContent()}</div>

              {/* Save/Cancel actions bottom - visible on mobile and desktop when editing */}
              {/*
              {isEditing && (
                <div className="mt-8 flex flex-col sm:flex-row justify-end gap-3">
                  <button className="px-4 py-2 rounded-md border" onClick={handleCancel}>Cancel</button>
                  <button className="px-4 py-2 rounded-md bg-primary-600 text-white" onClick={handleSave}>{isSaving ? 'Saving...' : 'Save All Changes'}</button>
                </div>
              )}
              */}
            </div>
          </main>
        </div>
      </div>
    </div >
  );
};

export default ManageProfilePage;
